import { Component, EventEmitter, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../dashboard.service';
import { TemplateService } from 'src/app/services/template.service';

export interface DialogData {
  _id: string;
  templateName: string;
  templateType: string;
  content: any;
  heading: string;
  update: boolean;
  subject: string;
  templatePlaceholder: string;
}

@Component({
  selector: 'app-create-update-template',
  templateUrl: './create-update-template.component.html',
  styleUrls: ['./create-update-template.component.scss']
})

export class CreateUpdateTemplateComponent implements OnInit, OnDestroy {
  onFileAdd = new EventEmitter();
  onLogoUpdated = new EventEmitter();

  public saveTemplateData: any;
  public events: string[] = [];
  logo: any;
  showVariables: boolean = true; // Default to true for the new sidebar
  variableDocs: Array<{ token: string; description: string; label: string; }> = [];
  copiedToken: string | null = null;

  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '500px',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    defaultFontSize: '',
    fonts: [
      { class: 'arial', name: 'Arial' },
      { class: 'times-new-roman', name: 'Times New Roman' },
      { class: 'calibri', name: 'Calibri' },
      { class: 'comic-sans-ms', name: 'Comic Sans MS' }
    ],
    sanitize: true,
    toolbarPosition: 'top',
    outline: true,
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript',
        'heading',
        'fontName'
      ],
      [
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'insertVideo',
        'removeFormat',
        'toggleEditorMode'
      ]
    ],
    customClasses: [
      {
        name: 'quote',
        class: 'quote',
      },
      {
        name: 'redText',
        class: 'redText'
      },
      {
        name: 'titleText',
        class: 'titleText',
        tag: 'h1',
      },
    ]
  };

  createUpdateTemplateForm = this.fb.group({
    templateName: ['', [Validators.required, this.trimRequiredValidator()]],
    subject: ['', [Validators.required, this.trimRequiredValidator()]],
    content: ['', [Validators.required, this.htmlContentRequiredValidator()]]
  });

  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<CreateUpdateTemplateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dashboardService: DashboardService, private fb: UntypedFormBuilder, private toastr: ToastrService,
    private templateService: TemplateService) { }

  ngOnInit() {
    // Initialize form based on template type
    if (this.data.templateType === 'WELCOME_TEMPLATE') {
      // For welcome templates, remove subject field
      this.createUpdateTemplateForm = this.fb.group({
        templateName: ['', [Validators.required, this.trimRequiredValidator()]],
        content: ['', [Validators.required, this.htmlContentRequiredValidator()]]
      });
    } else {
      // For email templates, include subject field
      this.createUpdateTemplateForm = this.fb.group({
        templateName: ['', [Validators.required, this.trimRequiredValidator()]],
        subject: ['', [Validators.required, this.trimRequiredValidator()]],
        content: ['', [Validators.required, this.htmlContentRequiredValidator()]]
      });
    }

    // Get logo for email templates
    if (this.data.templateType === 'EMAIL_TEMPLATE') {
      this.getLogo();
    }

    // Patch form values
    this.createUpdateTemplateForm.patchValue({
      templateName: this.data.templateName || '',
      subject: this.data.subject || '',
      content: this.data.content || ''
    });

    // Load variable docs for the current template type
    this.variableDocs = this.templateService.getTemplateVariableDocs(this.data.templateType);
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedToken = text;
      setTimeout(() => {
        if (this.copiedToken === text) {
          this.copiedToken = null;
        }
      }, 2000);
    }).catch(err => {
      this.toastr.error('Failed to copy text.');
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  saveTemplate() {
    if (this.createUpdateTemplateForm.invalid) {
      this.createUpdateTemplateForm.markAllAsTouched();
      return;
    }
    const templateReq = {
      templateName: this.createUpdateTemplateForm.get('templateName').value,
      subject: this.data.templateType === 'EMAIL_TEMPLATE' ? this.createUpdateTemplateForm.get('subject').value : '',
      templateType: this.data.templateType,
      content: this.createUpdateTemplateForm.get('content').value
      // logoImage: this.logo
    };
    let templateType = this.data.templateType === 'EMAIL_TEMPLATE' ? 'Email' : 'Welcome';
    this.subscriptions.push(this.dashboardService.createTemplate(templateReq).subscribe((res) => {
      this.dialogRef.close(res);
      // Success message handled by parent component
    }, () => {
      this.toastr.error(`Creation of the template has been failed. Try Again!`);
    }));
  }

  updateTemplate() {
    if (this.createUpdateTemplateForm.invalid) {
      this.createUpdateTemplateForm.markAllAsTouched();
      return;
    }
    const updateReq = {
      templateName: this.createUpdateTemplateForm.get('templateName').value,
      subject: this.data.templateType === 'EMAIL_TEMPLATE' ? this.createUpdateTemplateForm.get('subject').value : '',
      templateType: this.data.templateType,
      content: this.createUpdateTemplateForm.get('content').value
      // logoImage: this.logo
    };
    this.subscriptions.push(this.dashboardService.updateTemplate(this.data._id, updateReq).subscribe((res) => {
      this.dialogRef.close(res);
      // Success message handled by parent component
    }, () => {
      this.toastr.error(`Template update failed. Retry.`);
    }));
  }


  addLogo() {
    this.onFileAdd.emit();
  }

  getLogo() {
    this.subscriptions.push(this.dashboardService.getLogo().subscribe((res: any) => {
      this.logo = res ? res.logo : '';
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }

  /** Validator: field must contain non-whitespace text when HTML removed */
  private htmlContentRequiredValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value === null || value === undefined) {
        return { contentBlank: true };
      }
      let raw = String(value);
      // Decode HTML entities (including numeric) safely using DOM when available
      try {
        if (typeof document !== 'undefined') {
          const el = document.createElement('div');
          // Remove resource-loading tags to prevent network requests
          const cleanRaw = raw.replace(/<(img|script|iframe|link|video|audio|source|track|embed|object)[^>]*>/gi, '');
          el.innerHTML = cleanRaw;
          raw = (el.textContent || el.innerText || '');
        }
      } catch {
        // Fallback: replace common entities and numeric references
        raw = raw
          .replace(/&#x[0-9a-fA-F]+;|&#\d+;/g, ' ') // numeric entities
          .replace(/&nbsp;|&amp;nbsp;/gi, ' ');
      }

      const text = raw
        // Remove leftover tags if any and normalize whitespace
        .replace(/<br\s*\/?>(?=\s*<\/p>)/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/[\u00A0\u200B\uFEFF]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text.length ? null : { contentBlank: true };
    };
  }

  /** Validator: required but trims whitespace */
  private trimRequiredValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      return value && String(value).trim().length > 0 ? null : { requiredTrimmed: true };
    };
  }

}
